import React, { useRef, useState } from "react";
import { GENDERS, ETHNICITIES, GRADE_LEVELS, CHILD_LIVING_SITUATIONS } from './form-constants';
import ControlledForm from "~/shared/form/ControlledForm";
import TextInput from "~/shared/form/TextInput";
import SelectInput from "~/shared/form/SelectInput";
import Page from "~/shared/Page";
import FormSection from "~/shared/form/FormSection";
import FloatingPanel from "~/shared/FloatingPanel";
import useNavbar from "./use-navbar";
import { useParams, useLocation } from 'react-router-dom';
import CheckboxInput from "~/shared/form/CheckboxInput";
import { useForm, useFormContext } from 'react-hook-form';
import FieldContext from "~/shared/form/FieldContext";
import axios from "axios";
import { getUserRole } from "~/shared/auth-utils";
import { AddressFields, FullNameFields } from "~/pages/family/FieldGroups";
import FormResponse from "~/shared/form/FormResponse";
import FormButton from "~/shared/form/FormButton";
import { Breadcrumb } from "react-bootstrap";
import BreadcrumbLink from "~/shared/BreadcrumbLink";
import { getSessionData, setSessionData } from "~/shared/utils";
import { printEligibilityPdf, printParticipantPdf } from "./print-pdfs";

export default function ChildPage({ addNew = false }) {
    const { search } = useLocation();
    const [editable, setEditable] = useState(search === "?edit");
    const { familyId, childId } = useParams();
    const [lastName, setLastName] = useState(getSessionData(`family/${familyId}`)?.lastName);
    const formHook = useForm(addNew ? {} : { defaultValues, disabled: !editable });
    const { watch } = formHook;
    const family = useRef();
    const currentChild = useRef();
    const homeLink = getUserRole() === "FAMILY" ? "/home" : `/family/${familyId}`;

    async function defaultValues() {
        const response = await axios.post(getUserRole() === "FAMILY" ? "family/load-profile" : "admin/family/get", { id: familyId });
        const { parent, children } = response.data;
        const child = children.find(c => c._id === childId);
        family.current = response.data;
        currentChild.current = child;
        setLastName(parent.lastName)
        setSessionData(`family/${familyId}`, { lastName: parent.lastName });

        if (child.sameAddressAsParent) {
            child.streetAddress = parent.streetAddress;
            child.aptNumber = parent.aptNumber;
            child.city = parent.city;
            child.state = parent.state;
            child.zipcode = parent.zipcode
        }

        return child;
    }

    async function viewEligibilityForm() {
        const response = currentChild.current.program ? await axios.get(`programs/get-eligibility-form/${currentChild.current.program._id}`) : null;
        await printEligibilityPdf(family.current, currentChild.current, response?.data);
    }

    function onSubmit(data) {
        delete data.program;
        if (!addNew) {
            delete data.socialSecurity;
        }
        if (!data.hasInsuranceProvider) {
            data.insuranceProviderName = "";
        }
        if (!data.hasAllergies) {
            data.allergiesDescription = "";
        }
        if (!data.hasOtherMedicalConditions) {
            data.otherMedicalConditionsDescription = "";
        }
        if (!data.isTakingOtherMedications) {
            data.otherMedicationsDescription = "";
        }
        if (!data.isSpecialNeeds) {
            data.specialNeedsDescription = "";
        }

        if (getUserRole() === "ADMIN") {
            data.id = familyId;
            if (addNew) {
                data.hasSignedMedicalStatement = true;
            }
        }

        if (addNew && data.noSSN) {
            data.socialSecurity = "";
        }
    }

    function onSuccess(_, form, data) {
        form.reset(data);
        form.reset(); // Clear optional fields which have been unchecked
        setEditable(false);
        currentChild.current = { ...currentChild.current, ...data };
    }

    return (
        <Page title={addNew ? "Add Child" : "View Child"} navBar={useNavbar()}>
            <div className="page-container">
                <Breadcrumb className="mb-3">
                    {getUserRole() === "FAMILY" ? <>
                        <BreadcrumbLink href={homeLink}>Home</BreadcrumbLink>
                        <Breadcrumb.Item active>{addNew ? "Add Child" : "View Child"}</Breadcrumb.Item>
                    </> : <>
                        <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
                        <BreadcrumbLink href="/admin/manage-families">Manage Families</BreadcrumbLink>
                        <BreadcrumbLink href={homeLink}>{lastName ? `${lastName} Family` : "..."}</BreadcrumbLink>
                        <Breadcrumb.Item active>{addNew ? "Add Child" : "View Child"}</Breadcrumb.Item>
                    </>}
                </Breadcrumb>
                <FloatingPanel title="Child Information">
                    <ControlledForm hook={formHook} onSubmit={onSubmit} post={`${getUserRole().toLowerCase()}/child/${addNew ? "add" : childId}`} onSuccess={onSuccess} redirect={addNew ? homeLink : undefined}>
                        {addNew && <p className="mb-4">
                            Fill out the form below to register a new child in the family.
                            Required fields are marked with a star (*).
                        </p>}
                        <div className="flex flex-col gap-3">
                            <FormSection row wrap gap={1}>
                                <FullNameFields />
                            </FormSection>
                            <FormSection row wrap gap={1}>
                                <TextInput label="Date of Birth" id="dateOfBirth" preset="birthDate" required />
                                {addNew && <TextInput label="Social Security Number" id="socialSecurity" preset="password" mask="999-99-9999" jitMasking minLength="11" invalid="Invalid social security number." disabled={watch("noSSN")} required>
                                    <CheckboxInput label="Child does not have SSN" id="noSSN" className="mt-2" deps="socialSecurity" />
                                </TextInput>}
                                <SelectInput label="Gender" id="gender" placeholder="Select gender" options={GENDERS} required />
                                <SelectInput label="Ethnicity" id="ethnicity" placeholder="Select ethnicity" options={ETHNICITIES} required />
                            </FormSection>
                            <CheckboxInput label="Child has same address as parent" id="sameAddressAsParent" value={true} noMargin />
                            <FormSection row wrap gap={1}>
                                <FieldContext disabled={watch("sameAddressAsParent")}>
                                    <AddressFields />
                                </FieldContext>
                            </FormSection>
                            <FormSection row wrap gap={1}>
                                <TextInput label="School Attending" id="school" required />
                                <SelectInput label="Grade Level" id="gradeLevel" placeholder="Select grade" options={GRADE_LEVELS} required />
                                <TextInput label="School ID Number" id="studentID" required />
                            </FormSection>
                            <CheckboxInput label="ESOL (English as a second language) Child" id="isESOL" />
                            <SelectInput label="Living Situation" id="livingSituation" placeholder="Select living situation" options={CHILD_LIVING_SITUATIONS} required />
                            <FormSection heading title="DFCS Checklist">
                                <div className="flex flex-col gap-1">
                                    <CheckboxInput label="The youth is a U.S. citizen or qualified alien" id="isUSCitizenOrQualifiedAlien" />
                                    <CheckboxInput label="The youth is a Georgia resident" id="isGAResident" />
                                    <CheckboxInput label="The youth is in Foster Care within the state of Georgia" id="inFosterCare" />
                                    <CheckboxInput label="The youth is 18 years old and currently enrolled in school (high school, GED program or equivalent, or post 
secondary institution) and will be enrolled in AND attend school during the upcoming academic year" id="is18AndEnrolledInSchool" />
                                    <CheckboxInput label="The youth is 18-19 years old and has a dependent child AND is the custodial parent" id="isAdultAndParent" />
                                    <CheckboxInput label="Receives Temporary Assistance for Needy Families (TANF)" id="receivesTANF" />
                                    <CheckboxInput label="Receives Supplemental Nutrition Assistance Program (SNAP)" id="receivesSNAP" />
                                    <CheckboxInput label="Receives Medicaid or Social Security Income (SSI)" id="receivesMedicaidOrSSI" />
                                    <CheckboxInput label="Reduced or free lunch program at school" id="receivesReducedOrFreeLunch">
                                        <span className="text-neutral-600 text-sm text-left -mt-1">
                                            Note: This eligibility is only for single youth eligibility.
                                            This is not applicable if the entire school population is awarded free lunch in universal eligibility.
                                        </span>
                                    </CheckboxInput>
                                    <CheckboxInput label="Receives Peachcare for Kids" id="receivesPeachcare" />
                                </div>
                            </FormSection>
                            <FormSection heading title="Medical Information">
                                <div className="flex flex-col gap-3">
                                    <OptionalTextInput
                                        checkboxLabel="Does the child have health insurance coverage?"
                                        textLabel="Insurance Provider Name (if applicable)" label="Insurance Provider Name"
                                        checkboxId="hasInsuranceProvider" textId="insuranceProviderName"
                                    />
                                    <OptionalTextInput
                                        checkboxLabel="Does the child have any allergies?"
                                        textLabel="If yes, please list them" label="Allergies"
                                        checkboxId="hasAllergies" textId="allergiesDescription" multiline maxLength={400}
                                    />
                                    <OptionalTextInput
                                        checkboxLabel="Does the child have any other medical conditions (disabilities, infections, viruses, diseases, etc)?"
                                        textLabel="If yes, please list them" label="Other Medical Conditions"
                                        checkboxId="hasOtherMedicalConditions" textId="otherMedicalConditionsDescription" multiline maxLength={800}
                                    />
                                    <OptionalTextInput
                                        checkboxLabel="Is the child currently taking any medications (prescribed and non-prescribed)?"
                                        textLabel="If yes, please list them" label="Medications"
                                        checkboxId="isTakingOtherMedications" textId="otherMedicationsDescription" multiline maxLength={400}
                                    />
                                    <OptionalTextInput
                                        checkboxLabel="Is the child a special needs child?"
                                        textLabel="If yes, please specify the child's special need(s)" label="Special Needs"
                                        checkboxId="isSpecialNeeds" textId="specialNeedsDescription" multiline maxLength={800}
                                    />
                                </div>
                            </FormSection>
                        </div>
                        <FormResponse />
                        <div className="mt-4">
                            {addNew ? <>
                                {getUserRole() !== "ADMIN" && <>
                                    <p className="text-start mb-2">
                                        By checking below, I certify the above information is true to the best of my knowledge.
                                        I authorize Next Generation Focus, Inc., to contact me if my child is injured and/or harmed in any way.
                                        I also authorize Next Generation Focus, Inc. to seek medical attention for my child if he or she is injured
                                        and/or harmed and needs immediate medical assistance at a local hospital or emergency care center.
                                        I certify that I and/or our family's insurance provider will be responsible for any financial medical costs
                                        that may be associated with all medical attention and treatment given to my child. In consideration of their
                                        granting my child the opportunity to participate in the Afterschool Care Program, I hereby release,
                                        indemnify and hold harmless the Division of Family and Children Services and Next Generation Focus, Inc.
                                        from any liability, claim or demand resulting from any legal medical attention and assistance that may be needed
                                        and provided as a result of an injury or harmful incident to my child.
                                    </p>
                                    <CheckboxInput label="I have read and agree to the above statement" id="hasSignedMedicalStatement" invalid="You must accept the terms above to continue." required />
                                    <br />
                                </>}
                                <FormButton family submit className="mx-auto" />
                            </> : (
                                <>
                                    <div className="btn-container justify-around">
                                        <FormButton family onClick={viewEligibilityForm}>View Eligibility Form</FormButton>
                                        <FormButton family onClick={() => printParticipantPdf(family.current, currentChild.current)}>View Participant Form</FormButton>
                                    </div>
                                    <div className="btn-container justify-around">
                                        {editable ? <>
                                            <FormButton family submit>Save</FormButton>
                                            <FormButton family onClick={() => (setEditable(false), formHook.reset())}>Cancel</FormButton>
                                        </> : (
                                            <FormButton family key="edit" onClick={() => setEditable(true)}>Edit</FormButton>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </ControlledForm>
                </FloatingPanel>
            </div>
        </Page>
    );
}

function OptionalTextInput({ checkboxLabel = "", checkboxId = "", textLabel = "", textId = "", label = "", maxLength, multiline = false }) {
    const { watch } = useFormContext();

    return <div>
        <CheckboxInput label={checkboxLabel} id={checkboxId} noMargin deps={textId} className="mb-1" />
        <TextInput label={label} hideLabel placeholder={textLabel} id={textId} multiline={multiline} maxLength={maxLength} invalid={maxLength ? `${label} cannot be longer than ${maxLength} characters.` : undefined} disabled={!watch(checkboxId)} required />
    </div>;
}